import { IonCard, IonIcon, IonItem, IonProgressBar } from '@ionic/react';
import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { JobMessages } from 'src/intl/JobMessages';
import { selectBleSyncStatus } from 'src/redux/ble/selectors';
import { sync, alertCircle, checkmarkCircle } from 'ionicons/icons';
import './JobSyncSlide.scss';
import { bleActions } from 'src/redux/ble/actions';
import { DqText } from 'src/components/dq-text';

interface JobSyncSlideProps {
  className?: string;
}

export const JobSyncSlide: React.FC<JobSyncSlideProps> = ({ className }) => {
  const { formatMessage } = useIntl();
  const syncStatus = useSelector(selectBleSyncStatus);
  const dispatch = useDispatch();
  const [iconProps, setIconProps] = React.useState<{ color: string; icon: string }>({ color: 'info', icon: sync });
  const [message, setMessage] = React.useState('');
  const isHidden = !syncStatus;

  const shouldHide = syncStatus?.status === 'error' || syncStatus?.status === 'success';
  // An effect that begins dismissing the card after sync action completes.
  React.useEffect(() => {
    let handle: number | undefined;
    if (shouldHide) {
      handle = window.setTimeout(() => {
        dispatch(bleActions.syncLogFileReset());
      }, 5000);
    }
    return () => {
      if (handle) window.clearTimeout(handle);
    };
  }, [dispatch, shouldHide]);

  // Sets the message text in response to changes in the syncStatus. Not `useMemo` because we don't want to update the message in some cases.
  React.useEffect(() => {
    switch (syncStatus?.status) {
      case 'transfer':
      case 'upload':
        setMessage(
          formatMessage(JobMessages.syncTransferMessage, {
            fileName: syncStatus.filename,
            transferStatus: ((syncStatus.progress ?? 0) * 100).toFixed(0),
          }),
        );
        return;
      case 'error':
        setMessage(formatMessage(JobMessages.errorJobUpload));
        return;
      case 'success':
        setMessage(formatMessage(JobMessages.successfulJobUpload));
        return;
    }
  }, [formatMessage, syncStatus?.filename, syncStatus?.progress, syncStatus?.status]);

  // Sets the icon props in response to changes in the syncStatus. Not `useMemo` because we don't want to update the icon in some cases.
  React.useEffect(() => {
    switch (syncStatus?.status) {
      case 'error':
        setIconProps({
          color: 'danger',
          icon: alertCircle,
        });
        return;
      case 'success':
        setIconProps({
          color: 'success',
          icon: checkmarkCircle,
        });
        return;
      case 'transfer':
      case 'upload':
        setIconProps({
          color: 'dark',
          icon: sync,
        });
        return;
    }
  }, [syncStatus?.status]);

  return (
    <IonCard className={classNames('job-sync-slide-card', isHidden && 'hidden', className)}>
      <IonItem lines="none">
        <IonIcon slot="start" {...iconProps} />
        <DqText variant="body">{message}</DqText>
      </IonItem>
      <IonProgressBar
        className={classNames(syncStatus?.progress === undefined && 'hidden')}
        color="secondary"
        value={syncStatus?.progress ?? 0}
        type={syncStatus?.status === 'upload' ? 'indeterminate' : 'determinate'}
      />
    </IonCard>
  );
};
